import { useStytchMember } from "@stytch/react/b2b";
import { useEffect, useState, useMemo } from "react";
import { Eye, Eye as EyeIcon, Download, Loader2 } from "lucide-react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { toBlobURL, fetchFile } from "@ffmpeg/util";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment-timezone";
import { Link, useSearchParams } from "react-router-dom";
import AudioPlayer from 'react-h5-audio-player';
import { fetchTicketJobsPage, useTicketJobsPaginated, useTicketJob } from "../../lib/api/tickets";
import {
  TicketRunSummary,
  TicketsSummary,
  Provider,
} from "../../lib/models/index";
import { TicketJob, TicketJobStatus } from "../../lib/models/tickets";
import { Button } from "../../components/ui/button";
import { Loader } from "../../components/ui/loader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { useToast } from "../../components/ui/use-toast";
import { DataTable, type DataTableColumn, type FilterStyle } from "@/components/ui/DataTable/index";
import { CallLog, fetchCallLogsPage, useCallLogsPaginated, useGetAudioUrl } from "@/lib/api/callLog";
import 'react-h5-audio-player/lib/styles.css';
import { useRef } from "react";
import { CommonDestinationTimezone } from "@/lib/utils";
import { useQueryParam } from "@/hooks/use-query-param";
import { FilterBar } from "./FilterBar";

export function CallLogsDashboard() {
  const [searchParams] = useSearchParams();
  const today = moment.tz(CommonDestinationTimezone).endOf("day");

  const [fromParam] = useQueryParam("from", today.clone().startOf("day").format("YYYY-MM-DD"));
  const [toParam] = useQueryParam("to", today.clone().endOf("day").format("YYYY-MM-DD"));
  const [pageParam, setPageParam] = useQueryParam("page", "1");

  const dateRange = { from: fromParam, to: toParam };
  const page = parseInt(pageParam);

  // Calculate if date range is longer than 14 days
  const calculatePageSize = () => {
    if (dateRange.from && dateRange.to) {
      const start = moment(dateRange.from);
      const end = moment(dateRange.to);
      const diffInDays = end.diff(start, 'days');
      return diffInDays > 14 ? 200 : undefined;
    }
    return undefined;
  };
  
  const pageSize = calculatePageSize();
  
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append("page", pageParam || "1");
    if (dateRange.from) params.append("startDate", dateRange.from);
    if (dateRange.to) params.append("endDate", dateRange.to);
    if (pageSize) params.append("pageSize", `${pageSize}`);
    return params;
  }, [page, dateRange.from, dateRange.to, pageSize]);
  
  const callLogsRes: any = useCallLogsPaginated({
    page,
    queryFn: () => fetchCallLogsPage(page, dateRange.from, dateRange.to, pageSize),
    queryParams
  });

  if ([callLogsRes].some((result) => result.isLoading)) {
    return (
      <div className="flex flex-col space-y-2">
        <Skeleton className="h-4 w-[250px]" />
        <Skeleton className="h-4 w-[200px]" />
      </div>
    );
  }

  if (
    [callLogsRes].some((result) => result.error || !result?.data?.success)
  ) {
    return (
      <div className="flex flex-col space-y-2">
        Failed to fetch call logs. Please refresh and try again
      </div>
    );
  }

  return (
    <DashboardView 
      info={callLogsRes?.data?.data || {}} 
      setPage={setPageParam}
      isLoading={callLogsRes.isFetching}
      showPagination={!!pageSize}
    />
  );
}

export function DashboardView({
  info,
  setPage,
  isLoading,
  showPagination,
}: {
  isLoading: boolean;
  setPage: any;
  info: {
    items: CallLog[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      pageSize: number;
    },
  };
  showPagination: boolean;
}) {
  const page = info.pagination.currentPage;
  const dashboardColumns: DataTableColumn<CallLog>[] = useMemo(
      () => [
        {
          accessorKey: "call_log_date",
          header: "Date",
          cell: ({ row }) => {
            return (
              <span className="">
                {moment
                  .tz(row.original.call_log_date, CommonDestinationTimezone)
                  .format("MM/DD/YYYY HH:mm a")}
              </span>
            );
          },
        },
        {
          accessorKey: "from_user",
          accessorFn: (row) => row?.from_user_name || row?.from_number || row?.from_user || "Unknown",
          header: "From",
          cell: (info) => {
            return (
              <span className="">
                {info.getValue() as string}
              </span>
            );
          },
        },
        {
          accessorKey: "to_user",
          header: "To",
          accessorFn: (row) => row?.to_user_name || row?.outgoing_number || row?.to_user || "Unknown",
          cell: (info) => {
            return (
              <span className="">
                {info.getValue() as string}
              </span>
            );
          },
        },
        {
          accessorKey: "Transcription",
          header: "Transcription",
          cell: ({ row }) => (
            <ViewTranscription callLog={row.original} />
          ),
        },
      ],
      []
  );

  return (
    <>
      <div className="container flex flex-row items-center gap-4 mb-2">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Call Logs Dashboard
        </h1>
        <div className="flex items-center gap-2">
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
              <span className="text-sm text-gray-500">Loading...</span>
            </>
          ) : (
            <span className="text-sm text-gray-400">Data loaded</span>
          )}
        </div>
      </div>
      <div className="flex items-start flex-col h-full">
        <div className="container flex flex-col">
          <FilterBar />
          <DataTable
            className="shadow-lg"
            stickyHeaders
            tableClassName="max-h-[50vh] overflow-y-scroll"
            data={info.items}
            columns={dashboardColumns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            enableColumnPinning
            enableExport
            exportFilename={(data) => `call-logs-${new Date().toISOString().split('T')[0]}.csv`}
            filterStyle='select'
            filterMode='internal'
          />
        </div>
        {/* Pagination controls - only show when needed */}
        {showPagination && (
          <div className="container flex items-center justify-between mt-4">
            <Button
              onClick={() => setPage(page - 1)}
              disabled={page <= 1 || isLoading}
            >
              {isLoading ? <Loader /> : <span>Previous</span>}
            </Button>

            <span>
              Page {page} of {info.pagination.totalPages}
            </span>

            <Button
              onClick={() => setPage(page + 1)}
              disabled={page >= info.pagination.totalPages || isLoading}
            >
              {isLoading ? <Loader /> : <span>Next</span>}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

function ViewTranscription({ callLog }: { callLog: CallLog }) {
  const [isOpen, setIsOpen] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const getAudioUrl = useGetAudioUrl();
  const { toast } = useToast();

  const handleGetAudio = async () => {
    if (audioUrl) return audioUrl;
    
    setIsLoadingAudio(true);
    try {
      const response = await getAudioUrl(callLog.id);
      if (response.success && response.data.link) {
        setAudioUrl(response.data.link);
        return response.data.link;
      } else {
        toast({
          title: "Failed to get audio",
          description: "Could not retrieve audio file",
          variant: "destructive",
        });
        return null;
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch audio",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const handlePlayAudio = async () => {
    const url = await handleGetAudio();
    if (url) {
      setShowPlayer(true);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      >
        <Eye className="w-4 h-4" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Call Transcription</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Call Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">From:</label>
                <p className="text-sm">
                  {getFrom(callLog)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">To:</label>
                <p className="text-sm">
                  {getTo(callLog)}
                </p>
              </div>
            </div>

            {/* Audio Player Section */}
            <div className="space-y-2">
              <div className="flex gap-2">
                <Button
                  onClick={handlePlayAudio}
                  disabled={isLoadingAudio || showPlayer}
                >
                  {isLoadingAudio ? <Loader /> : "Play Audio"}
                </Button>
                
                <DownloadButton 
                  callLog={callLog}
                  audioUrl={audioUrl}
                  onGetAudioUrl={handleGetAudio}
                  isLoadingAudio={isLoadingAudio}
                />
              </div>

              {showPlayer && audioUrl && (
                <div className="space-y-2">
                  <AudioConverter presignedUrl={audioUrl} />
                </div>
              )}
            </div>

            {/* Transcription Section */}
            <Accordion type="single" collapsible defaultValue="cleaned">
              <AccordionItem value="original">
                <AccordionTrigger>Original Transcription</AccordionTrigger>
                <AccordionContent>
                  <div className="text-sm whitespace-pre-wrap">
                    {callLog.transcribed_meta_data?.reason ? (callLog.transcribed_meta_data?.reason) : (callLog.transcribed_meta_data?.data || "No transcription available")}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="cleaned">
                <AccordionTrigger>Cleaned Transcription</AccordionTrigger>
                <AccordionContent>
                  <div className="text-sm whitespace-pre-wrap">
                    {callLog.cleaned_meta_data?.data || "No cleaned transcription available"}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DownloadButton({ 
  callLog, 
  audioUrl, 
  onGetAudioUrl, 
  isLoadingAudio 
}: { 
  callLog: CallLog;
  audioUrl: string | null;
  onGetAudioUrl: () => Promise<string | null>;
  isLoadingAudio: boolean;
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const { toast } = useToast();

  const handleDownload = async () => {
    setIsDownloading(true);
    setShowFallback(false);

    try {
      // Get the audio URL if we don't have it yet
      let url = audioUrl;
      if (!url) {
        url = await onGetAudioUrl();
        if (!url) {
          setIsDownloading(false);
          return;
        }
      }

      // Try to extract filename from URL or use default
      const urlParts = url.split('/');
      const urlFilename = urlParts[urlParts.length - 1].split('?')[0];
      const filename = urlFilename || `call-recording-${callLog.id}`;

      // Method 1: Try using iframe for automatic download
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);

      // Show fallback after 2 seconds if download hasn't started
      setTimeout(() => {
        setShowFallback(true);
        document.body.removeChild(iframe);
      }, 2000);

      // Method 2: Also try fetch + blob approach for better browser support
      try {
        const response = await fetch(url);
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          
          // Cleanup
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          }, 100);
          
          toast({
            title: "Download started",
            description: "Your audio file is downloading",
          });
          
          setShowFallback(false);
        }
      } catch (fetchError) {
        // If fetch fails due to CORS, show fallback
        console.log('Fetch failed, showing fallback');
        setShowFallback(true);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: "Please try the fallback download link",
        variant: "destructive",
      });
      setShowFallback(true);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        onClick={handleDownload}
        disabled={isLoadingAudio || isDownloading}
      >
        {isDownloading ? (
          <Loader className="w-4 h-4" />
        ) : (
          <>
            <Download className="w-4 h-4 mr-2" />
            Download Audio
          </>
        )}
      </Button>

      {showFallback && audioUrl && (
        <a
          href={audioUrl}
          download={`call-recording-${callLog.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Click here if download didn't start
        </a>
      )}
    </div>
  );
}

const AudioConverter = ({ presignedUrl }: { presignedUrl: string }) => {
  const ffmpegRef = useRef(new FFmpeg());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mp3Url, setMp3Url] = useState<string | null>(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const playerRef = useRef<null | any>(null);

  useEffect(() => {
    convertToMp3();
  }, [presignedUrl]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (mp3Url) {
        URL.revokeObjectURL(mp3Url);
      }
    };
  }, [mp3Url]);

  const handleSpeedChange = (speed: number) => {
    if (playerRef.current) {
      playerRef.current.audio.current.playbackRate = speed;
      setPlaybackRate(speed);
    }
  };


  const convertToMp3 = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load ffmpeg
      if (!ffmpegRef.current.loaded) {
        await ffmpegRef.current.load();
      }

      const ffmpeg = ffmpegRef.current;

      await ffmpeg.writeFile('input.wav', await fetchFile(presignedUrl));

      // Run the conversion
      await ffmpeg.exec(['-i', 'input.wav', 'output.mp3']);

      // Read the result
      const mp3Data = await ffmpeg.readFile('output.mp3');

      // Create a blob URL for the MP3
      const uint8Array = typeof mp3Data === 'string'
        ? new TextEncoder().encode(mp3Data)
        : new Uint8Array(mp3Data as any);

      const blob = new Blob([uint8Array.buffer], { type: 'audio/mp3' });
      const url = URL.createObjectURL(blob);

      setMp3Url(url);
      setLoading(false);

      await new Promise(res => setTimeout(res, 100));
      try {
        await ffmpeg.deleteFile('input.wav');
      } catch (error) {
        console.warn("Failed to delete input")
      }

      try {
        await ffmpeg.deleteFile('output.mp3');
      } catch (error) {
        console.warn("Failed to delete output")
      }
    } catch (err) {
      console.error('Conversion error:', err);
      setError('Failed to convert audio file');
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Converting audio file...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="space-y-2">
      <AudioPlayer
        ref={playerRef}
        src={mp3Url!}
        preload={"metadata"}
        autoPlay={false}
        showJumpControls={true}
        customControlsSection={['MAIN_CONTROLS', 'VOLUME_CONTROLS'] as any[]}
        onError={(e) => {
          console.error('Convert Audio Player Error:', e);
        }}
        onPlayError={(e) => {
          console.error('Convert Audio Play Error:', e);
        }}
        onChangeCurrentTimeError={(e) => {
          console.error('Convert Audio Change Time Error:', e);
        }}
      />
      <div className="flex justify-center gap-2">
        <Button
          variant={playbackRate === 1 ? "default" : "outline"}
          size="sm"
          onClick={() => handleSpeedChange(1)}
        >
          1x
        </Button>
        <Button
          variant={playbackRate === 1.25 ? "default" : "outline"}
          size="sm"
          onClick={() => handleSpeedChange(1.25)}
        >
          1.5x
        </Button>
        <Button
          variant={playbackRate === 1.5 ? "default" : "outline"}
          size="sm"
          onClick={() => handleSpeedChange(1.5)}
        >
          2x
        </Button>
      </div>
    </div>
  );
};

function getTo(item: CallLog) {
  return item.to_user_name || item.outgoing_number || item.to_user || "Unknown";
}

function getFrom(item: CallLog) {
  return item.from_user_name || item.from_number || item.from_user || "Unknown";
}
